import React, { useState } from 'react'
import { Box, Button, Dialog, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'
import { formatDay } from '../../format'
import { advancedColors } from '../../theme'
import { AcceptanceCriterion, upcomingMondays } from './trackAcceptance'

// `startsNow`: no Monday to pick — the track runs from the moment it is accepted (ADR-098).
export type AcceptRequest = {
  title: string
  criteria: AcceptanceCriterion[]
  startsNow: boolean
  confirm: (startsOn: string | null) => void
}

type Props = {
  request: AcceptRequest | null
  onCancel: () => void
}

const AcceptTrackForm = ({ request, onCancel }: Omit<Props, 'request'> & { request: AcceptRequest }) => {
  const mondays = upcomingMondays()
  const [startsOn, setStartsOn] = useState(mondays[0])
  const ready = request.criteria.every((criterion) => criterion.met)

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{request.startsNow ? `Start ${request.title}` : `Accept ${request.title}`}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ color: advancedColors.inkMuted, mb: 1 }}>
          Before it can start
        </Typography>
        {request.criteria.map((criterion) => (
          <Box key={criterion.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {criterion.met ? (
              <CheckCircleRoundedIcon fontSize="small" sx={{ color: advancedColors.green }} />
            ) : (
              <RadioButtonUncheckedRoundedIcon fontSize="small" sx={{ color: advancedColors.inkFaint }} />
            )}
            <Typography variant="body2">
              {criterion.label}
              {!criterion.met && ' (missing)'}
            </Typography>
          </Box>
        ))}
        {!request.startsNow && (
          <TextField
            select
            size="small"
            fullWidth
            label="Starts on"
            value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)}
            sx={{ mt: 2 }}
          >
            {mondays.map((monday) => (
              <MenuItem key={monday} value={monday}>
                Monday {formatDay(monday)}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!ready}
            onClick={() => request.confirm(request.startsNow ? null : startsOn)}
          >
            {request.startsNow ? 'Start now' : 'Accept'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

const AcceptTrackDialog = ({ request, ...rest }: Props) =>
  request ? <AcceptTrackForm request={request} {...rest} /> : null

export default AcceptTrackDialog
